import axios, { AxiosResponse } from 'axios';
import { Currency } from 'dinero.js';

/**
 * Credit limit status from Aria API
 */
export type CreditLimitStatus = 'ACCEPTED' | 'REVISED' | 'PROCESSING' | 'ONGOING_REVISION' | 'REFUSED' | null;

/**
 * Business provider status from Aria API
 */
export type AriaProviderStatus = 'pending' | 'in_review' | 'accepted' | 'refused' | null;

export const ARIA_PSP_NAME = 'aria';
export const AKS_PSP_NAME = 'aks';

/**
 * Aria refusal reasons that trigger the BI update flow. All remediable via updating the business identifier.
 * - `DEBTOR_CLOSED` / `DEBTOR_NOT_FOUND`: post-split reasons (Aria now emits either)
 * - `DEBTOR_NOT_FOUND_OR_CLOSED`: legacy pre-split reason, kept for backward compatibility until Aria fully deploys the split
 */
export const REFUSAL_REASONS_DEBTOR_CLOSED: readonly string[] = [
  'DEBTOR_CLOSED',
  'DEBTOR_NOT_FOUND',
  'DEBTOR_NOT_FOUND_OR_CLOSED',
];

/**
 * Onboarding document status
 */
type OnboardingDocumentStatus = 'pending' | 'approved' | 'accepted' | 'refused';

interface DebtorIdentifier {
  country: string;
  type: string;
  value: string;
}

export interface AnkorpayMandateData {
  debtorId: string;
  debtorIban: string;
  debtorIdentifier: DebtorIdentifier;
  signatureDate?: string;
}

interface AnkorpayPaymentMethod {
  scheme: string;
  status: string;
  mandate?: AnkorpayMandateData;
}

export interface AnkorpayOnboardingDocument {
  type: 'ankorpay-onboarding-document';
  id: string;
  attributes: {
    pspDocumentId: string;
    documentType: string;
    status: OnboardingDocumentStatus;
    refusalReasons?: string[];
    pspCreatedAt: string;
    pspUpdatedAt: string;
  };
}

export interface AnkorpayBusinessProvider {
  type: 'ankorpay-business-provider';
  id: string;
  attributes: {
    kycLink: string | null;
    psp: string;
    pspId: string;
    status: AriaProviderStatus;
  };
  relationships?: {
    documents?: {
      data: Array<{ type: string; id: string }> | null;
    };
  };
}

export interface AnkorpayCreditLimit {
  type: 'credit-limit';
  id: string;
  attributes: {
    availableAmount: {
      value: number;
      currency: Currency;
    };
    status: string | null;
    totalAmount: {
      value: number;
      currency: Currency;
    };
  };
}

export interface AnkorpayAvailableCreditResponse {
  data: {
    type: 'credit-limit';
    id: string;
    attributes: {
      status: CreditLimitStatus;
      frozenAt: string | null;
      refusalReasons?: string[] | null;
      totalAmount: {
        value: number;
        currency: Currency;
      };
      availableAmount: {
        value: number;
        currency: Currency;
      };
    };
  };
}

export interface AnkorpayBusinessResponse {
  data: {
    type: string;
    id: string;
    attributes: {
      companyId: string;
      countryCode: string;
      externalId: string;
      externalType?: string;
    };
    relationships: {
      providers: {
        data: Array<{
          type: string;
          id: string;
        }>;
      };
      creditLimit?: {
        data: {
          type: string;
          id: string;
        };
      };
    };
  };
  included?: (AnkorpayBusinessProvider | AnkorpayCreditLimit | AnkorpayOnboardingDocument)[];
}

export interface AnkorpayPaymentMethodsResponse {
  data: Array<{
    type: string;
    attributes: {
      psp: string;
      methods: AnkorpayPaymentMethod[];
    };
  }>;
}

export type AnkorpayBusinessInclude = 'providers' | 'creditLimit' | 'providers.documents';

/**
 * Fetches business data with providers and/or credit limit from Ankorpay for a retailer
 */
export const getAnkorpayBusiness = async (
  retailerUuid: string,
  include: AnkorpayBusinessInclude[] = ['providers']
): Promise<AnkorpayBusinessResponse> => {
  const includeParam = include.join(',');
  const response = await axios.get<AnkorpayBusinessResponse>(
    `/api/ankorpay/v1/businesses/retailer/${retailerUuid}?include=${includeParam}`
  );
  return response.data;
};

/**
 * Fetches payment methods from Ankorpay for a retailer
 */
export const getAnkorpayPaymentMethods = async (retailerUuid: string): Promise<AnkorpayPaymentMethodsResponse> => {
  const response = await axios.get<AnkorpayPaymentMethodsResponse>(
    `/api/ankorpay/v1/businesses/retailer/${retailerUuid}/payment-methods`
  );
  return response.data;
};

/**
 * Cancels all old payment methods for a retailer, keeping only the most recent active one.
 * The backend automatically handles the logic:
 * - Lists all ACTIVE payment methods
 * - Sorts by creation date
 * - Cancels all but the newest one
 * - Ignores PENDING_SIGNATURE and other non-ACTIVE statuses
 */
export const cancelOldAnkorpayPaymentMethods = async (retailerUuid: string): Promise<void> => {
  await axios.post(`/api/ankorpay/v1/businesses/retailer/${retailerUuid}/payment-methods/-actions/cancel`);
};

export const getAnkorpayAvailableCredit = (): Promise<AxiosResponse<AnkorpayAvailableCreditResponse>> =>
  axios.get<AnkorpayAvailableCreditResponse>(`/api/ankorpay/v1/buyers/credit-limits`);

type AnkorpayIncludedItem = AnkorpayBusinessProvider | AnkorpayCreditLimit | AnkorpayOnboardingDocument;

/**
 * Type guard to check if an included item is a business provider
 */
export const isBusinessProvider = (item: AnkorpayIncludedItem): item is AnkorpayBusinessProvider => {
  return item.type === 'ankorpay-business-provider';
};

/**
 * Extracts Aria provider from Ankorpay business response
 * Returns the provider where psp is 'aria'
 */
export const getAriaProvider = (response: AnkorpayBusinessResponse): AnkorpayBusinessProvider | null => {
  if (!response.included || response.included.length === 0) {
    return null;
  }

  return (
    response.included.filter(isBusinessProvider).find((provider) => provider.attributes.psp?.toLowerCase() === ARIA_PSP_NAME) ||
    null
  );
};

/**
 * Extracts Aria debtor ID from Ankorpay business response
 * Returns the pspId from the provider where psp is 'aria'
 */
export const getAriaDebtorId = (response: AnkorpayBusinessResponse): string | null => {
  const ariaProvider = getAriaProvider(response);
  return ariaProvider?.attributes.pspId || null;
};

/**
 * Extracts Aria KYC link from Ankorpay business response
 * Returns the kycLink from the provider where psp is 'aria'
 */
export const getAriaKycLink = (response: AnkorpayBusinessResponse): string | null => {
  const ariaProvider = getAriaProvider(response);
  return ariaProvider?.attributes.kycLink || null;
};

/**
 * Extracts Aria SEPA mandate data from Ankorpay response
 * Handles both lowercase and uppercase values from API (e.g., 'SEPA_DEBIT', 'ACTIVE')
 */
export const getAriaMandateData = (response: AnkorpayPaymentMethodsResponse): AnkorpayMandateData | null => {
  for (const resource of response.data) {
    const { attributes } = resource;

    if (attributes.psp?.toLowerCase() !== 'aria') {
      continue;
    }

    for (const method of attributes.methods || []) {
      const scheme = method.scheme?.toUpperCase();
      const status = method.status?.toUpperCase();

      if ((scheme === 'SEPA_DIRECT_DEBIT' || scheme === 'SEPA_DEBIT') && status === 'ACTIVE' && method.mandate) {
        return method.mandate;
      }
    }
  }

  return null;
};

/**
 * Type guard to check if an included item is a credit limit
 */
export const isCreditLimit = (item: AnkorpayIncludedItem): item is AnkorpayCreditLimit => {
  return item.type === 'credit-limit';
};

/**
 * Type guard to check if an included item is an onboarding document
 */
export const isOnboardingDocument = (item: AnkorpayIncludedItem): item is AnkorpayOnboardingDocument => {
  return item.type === 'ankorpay-onboarding-document';
};

/**
 * Extracts credit limit data from Ankorpay business response
 */
export const getCreditLimit = (response: AnkorpayBusinessResponse): AnkorpayCreditLimit | null => {
  return response.included?.find(isCreditLimit) ?? null;
};

/**
 * Extracts onboarding documents from Ankorpay business response
 */
export const getOnboardingDocuments = (response: AnkorpayBusinessResponse): AnkorpayOnboardingDocument[] => {
  return response.included?.filter(isOnboardingDocument) ?? [];
};

/**
 * Extracts Aria provider status from Ankorpay business response
 */
export const getAriaProviderStatus = (response: AnkorpayBusinessResponse): AriaProviderStatus => {
  const ariaProvider = getAriaProvider(response);
  return ariaProvider?.attributes.status ?? null;
};
